import { matchPath } from "react-router";

import { getOrganizationId } from "Libs/utils";
import { routerLocationChange } from "Reducers/app/routerLocationChange";
import {
  selectEnvironmentTaskByName,
  selectEnvironmentTasksHasLoaded
} from "Reducers/environment/environmentTasks";
import {
  getProjectAddons,
  projectAddonsLoadingSelector,
  projectAddonsSelector
} from "Reducers/organization/projectAddons";
import { startAppListening } from "Store/listenerMiddleware";

import { RUN_TASK_PARAM } from "../../useRunTaskParam";

import { isPerformanceAgentTask } from "./config";

import type { Location } from "react-router";
import type { RootState } from "Store/configureStore";
import type { AppStartListening } from "Store/listenerMiddleware";

/** Time budget for the organization and the task list to land in state. */
const PREREQUISITES_TIMEOUT_MS = 15_000;

/**
 * Every environment page — the Run modal only ever opens over one. The data
 * router (`routes.console.ts`) matches down to `:organizationId/*` and renders
 * the rest through descendant `<Routes>`, so `routerLocationChange.params` never
 * carries project or environment; re-derive them from the pathname with the same
 * matcher the router uses (cf. `git.listeners`).
 */
const ENVIRONMENT_ROUTE_PATTERN =
  "/:organizationId/:projectId/:environmentId/*";

/** The bookmarkable opener. The in-place ones push `?run=<taskName>` instead. */
const RUN_DEEP_LINK_PATTERN =
  "/:organizationId/:projectId/:environmentId/tasks/:taskName/run";

/** The task whose Run modal this location opens, from either opener. */
const getRunTaskName = (location: Location) =>
  matchPath(RUN_DEEP_LINK_PATTERN, location.pathname)?.params.taskName ??
  new URLSearchParams(location.search).get(RUN_TASK_PARAM);

/**
 * Loads the project add-ons the performance-agent form's timeframe gate reads,
 * on the navigation that opens its Run modal — so the form itself fetches
 * nothing.
 *
 * Opening the modal is always a URL change: the in-place buttons push
 * `?run=<taskName>` (`useRunTaskParam`) and the deep link is
 * `.../tasks/:taskName/run`, so one location listener covers every opener.
 * Without a fetch here the gate falls back to the subscription and reports
 * "free" to a customer who bought continuous profiling — no ancestor of the
 * environment overview, the task detail page, or the deep link loads add-ons.
 *
 * Scoped to performance-agent tasks and kept in this feature's folder rather
 * than with the `projectAddons` slice: the modal is the only consumer, so no
 * other task's Run modal fetches anything and removing the feature removes the
 * policy with it.
 */
export const registerPerformanceAgentListeners = (
  startListening: AppStartListening
) => {
  startListening({
    actionCreator: routerLocationChange,
    effect: async (
      action,
      { dispatch, getState, condition, cancelActiveListeners }
    ) => {
      const { location } = action.payload;

      const taskName = getRunTaskName(location);
      if (!taskName) return;

      const match = matchPath(ENVIRONMENT_ROUTE_PATTERN, location.pathname);
      if (!match) return;

      const { organizationId, projectId, environmentId } = match.params;
      if (!organizationId || !projectId || !environmentId) return;

      // Supersede an in-flight wait from a previous navigation.
      cancelActiveListeners();

      const params = { organizationId, projectId, environmentId };

      // Two prerequisites, neither guaranteed at navigation time (on a deep
      // link, neither is present): the add-ons endpoint is keyed by the
      // organization's id while the URL carries its name, and the task's TYPE —
      // which decides whether this modal is the agent's — arrives with the task
      // list's own fetch.
      const isReady = (state: RootState) =>
        Boolean(getOrganizationId(state, organizationId)) &&
        selectEnvironmentTasksHasLoaded(state, params);

      if (!isReady(getState())) {
        const ready = await condition(
          (_, currentState) => isReady(currentState),
          PREREQUISITES_TIMEOUT_MS
        );
        if (!ready) return;
      }

      const state = getState();
      const organizationRealId = getOrganizationId(state, organizationId);
      const task = selectEnvironmentTaskByName(state, { ...params, taskName });

      if (!organizationRealId || !isPerformanceAgentTask(task?.type)) return;

      // One fetch per project. A failure leaves no data, so the next open
      // retries; an in-console purchase writes the store directly.
      const isAlreadyKnown =
        Boolean(projectAddonsSelector(state, { projectId })) ||
        projectAddonsLoadingSelector(state, { projectId });
      if (isAlreadyKnown) return;

      dispatch(
        getProjectAddons({ organizationId: organizationRealId, projectId })
      );
    }
  });
};

// Register against the production middleware singleton.
registerPerformanceAgentListeners(startAppListening);
